import { useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Banknote, Calendar, MessageCircle, Phone, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { chatTitle, moveCrmStage, shownPhone, type CrmCard, type CrmFunnel } from '@/entities/chats';
import { ApiError } from '@/shared/api';
import { formatBoardDate, crmName, labelOf, useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import { Textarea } from '@/shared/ui/shadcn/textarea';

function sameStage(left: string, right: string) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function columnsFor(funnel: CrmFunnel, cards: CrmCard[]) {
  const stages = [...funnel.stages];
  for (const card of cards) {
    if (card.stage && !stages.some((stage) => sameStage(stage, card.stage))) {
      stages.push(card.stage);
    }
  }
  return stages;
}

function contactLine(card: CrmCard, channel: string) {
  const title = chatTitle(card).trim().toLowerCase();
  const phone = shownPhone(card.phone, card.channel_chat_external_id);
  const parts = [phone && phone.toLowerCase() !== title ? phone : '', channel].filter(Boolean);
  return parts.join(' · ');
}

function budgetOf(card: CrmCard) {
  if (card.price && card.price > 0) {
    return `${card.price.toLocaleString('ru-RU')} ฿`;
  }
  return '';
}

function MetaRow({ icon: Icon, children }: { icon: typeof Phone; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <Icon className="size-3.5 shrink-0 opacity-70" aria-hidden />
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
}

function CardBody({ card, dragging }: { card: CrmCard; dragging?: boolean }) {
  const { t, locale } = useI18n();
  const contact = contactLine(card, labelOf(t.channels, card.channel));
  const budget = budgetOf(card);
  const date = formatBoardDate(card.updated_at, locale);
  const summary = card.summary?.trim() ?? '';
  const lastComment = card.comments?.length
    ? card.comments[card.comments.length - 1]?.text?.trim()
    : '';
  return (
    <div
      className={cn(
        'rounded-xl border border-border/80 bg-background p-3 shadow-none',
        dragging && 'shadow-md ring-1 ring-border',
      )}
    >
      <p className="truncate text-sm font-medium tracking-tight">
        {chatTitle(card) || t.common.lead}
      </p>
      <div className="mt-2 flex flex-col gap-1.5">
        {budget ? (
          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            <Banknote className="size-3.5 shrink-0 text-muted-foreground opacity-70" aria-hidden />
            <span className="min-w-0 truncate font-medium">{budget}</span>
          </div>
        ) : null}
        {date ? <MetaRow icon={Calendar}>{date}</MetaRow> : null}
        {card.responsible_name ? <MetaRow icon={User}>{card.responsible_name}</MetaRow> : null}
        {contact ? <MetaRow icon={Phone}>{contact}</MetaRow> : null}
        {lastComment ? <MetaRow icon={MessageCircle}>{lastComment}</MetaRow> : null}
      </div>
      {summary ? (
        <>
          <div className="my-2.5 h-px bg-border/70" />
          <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{summary}</p>
        </>
      ) : null}
    </div>
  );
}

function DraggableCard({ card, onOpen }: { card: CrmCard; onOpen: (card: CrmCard) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    data: { card },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(card)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen(card);
      }}
      className={cn(
        'cursor-grab touch-none outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDragging && 'opacity-40',
      )}
    >
      <CardBody card={card} />
    </div>
  );
}

function Column({
  stage,
  cards,
  onOpen,
}: {
  stage: string;
  cards: CrmCard[];
  onOpen: (card: CrmCard) => void;
}) {
  const { t } = useI18n();
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <section
      ref={setNodeRef}
      className={cn(
        'flex h-full w-[85vw] shrink-0 flex-col rounded-xl bg-muted/35 desktop:w-72',
        isOver && 'bg-muted/55 ring-1 ring-border',
      )}
    >
      <header className="flex items-center gap-2 px-3 pt-3 pb-2">
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium tracking-tight">
          {crmName(t.crm.names, stage)}
        </h2>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{cards.length}</span>
      </header>
      <ScrollArea className="min-h-0 flex-1" viewportClassName="px-2 pb-2">
        <div className="flex flex-col gap-2">
          {cards.length === 0 ? (
            <p className="px-1 py-8 text-center text-xs text-muted-foreground">{t.crm.noCards}</p>
          ) : (
            cards.map((card) => <DraggableCard key={card.id} card={card} onOpen={onOpen} />)
          )}
        </div>
      </ScrollArea>
    </section>
  );
}

export function CrmBoard({ funnel, cards }: { funnel: CrmFunnel; cards: CrmCard[] }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stages = columnsFor(funnel, cards);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pending, setPending] = useState<{ card: CrmCard; stage: string } | null>(null);
  const [note, setNote] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const activeCard = activeId ? cards.find((card) => card.id === activeId) : null;

  const move = useMutation({
    mutationFn: (input: { cardId: string; stage: string; note: string }) =>
      moveCrmStage(input.cardId, { stage: input.stage, note: input.note }),
    onSuccess: async () => {
      setPending(null);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['crm-cards'] });
      await queryClient.invalidateQueries({ queryKey: ['sales-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.moveFailed);
    },
  });

  const openCard = (card: CrmCard) => {
    navigate(`/crm/cards/${card.id}?pipeline=${encodeURIComponent(funnel.name)}`);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const card = event.active.data.current?.card as CrmCard | undefined;
    const target = event.over?.id;
    if (!card || typeof target !== 'string') return;
    if (!stages.some((stage) => sameStage(stage, target))) return;
    if (sameStage(target, card.stage)) return;
    const stage = stages.find((item) => sameStage(item, target)) ?? target;
    setNote('');
    setPending({ card, stage });
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={(event: DragStartEvent) => setActiveId(String(event.active.id))}
        onDragCancel={() => setActiveId(null)}
        onDragEnd={handleDragEnd}
      >
        <ScrollArea className="h-full min-h-0" viewportClassName="p-3 desktop:p-4">
          <div className="flex h-full min-h-0 w-max gap-3">
            {stages.map((stage) => (
              <Column
                key={stage}
                stage={stage}
                cards={cards.filter((card) => sameStage(card.stage, stage))}
                onOpen={openCard}
              />
            ))}
          </div>
        </ScrollArea>
        <DragOverlay dropAnimation={null}>
          {activeCard ? (
            <div className="w-72">
              <CardBody card={activeCard} dragging />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !move.isPending) {
            setPending(null);
            setNote('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.crm.whatChanged}</DialogTitle>
            <DialogDescription>
              {pending
                ? `${crmName(t.crm.names, pending.card.stage)} → ${crmName(t.crm.names, pending.stage)}`
                : null}
              {pending ? ' ' : null}
              {t.crm.whatChangedHint}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t.crm.notePlaceholder}
            rows={4}
            autoFocus
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={move.isPending}
              onClick={() => {
                setPending(null);
                setNote('');
              }}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              disabled={move.isPending || note.trim() === ''}
              onClick={() => {
                if (!pending) return;
                move.mutate({
                  cardId: pending.card.id,
                  stage: pending.stage,
                  note: note.trim(),
                });
              }}
            >
              {move.isPending ? t.common.saving : t.crm.saveNote}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
